import React, { useState, useEffect } from 'react';
import { Modal, Typography, Button, Steps, Form, Input, Select, Row, Col, Collapse, Space, Divider, message, Spin, Upload } from 'antd';
import { 
  CloseOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  UserOutlined,
  HomeOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
  SunOutlined,
  DownloadOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { useCognitoAuth } from '../contexts/CognitoAuthContext';
import { api } from '../services/api';
import solarServerApi from '../services/solarServerApi';
import { IframeService } from '../services/iframeService';
import './SolarWizardModal.css';

const { Title, Text } = Typography;
const { Option } = Select;

// Component to check for existing uploaded documents
const CheckExistingDocument: React.FC<{ clientId: number; onDocumentFound: (file: File) => void; refreshTrigger?: number }> = ({ clientId, onDocumentFound, refreshTrigger }) => {
  const [loading, setLoading] = useState(true);
  const [documentExists, setDocumentExists] = useState(false);
  const [documentInfo, setDocumentInfo] = useState<any>(null);

  useEffect(() => {
    checkExistingDocument();
  }, [clientId, refreshTrigger]);

  const checkExistingDocument = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/iframe/check-simulation-document/${clientId}`);
      
      if (response.data.exists) {
        setDocumentExists(true);
        setDocumentInfo(response.data.document_info);
        
        // Create a fake file object for the form
        const fakeFile = {
          name: response.data.document_info.filename || 'simulation-document.pdf',
          size: response.data.document_info.file_size || 0,
          type: 'application/pdf',
          lastModified: new Date(response.data.document_info.uploaded_at).getTime()
        } as File;
        
        onDocumentFound(fakeFile);
      } else {
        setDocumentExists(false);
        setDocumentInfo(null);
      }
    } catch (error: any) {
      setDocumentExists(false);
      setDocumentInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      // Use a direct download approach instead of opening in new tab
      const response = await api.get(`/api/iframe/download-simulation-document/${clientId}`, {
        responseType: 'blob'
      });
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = documentInfo?.filename || `simulation-document-${clientId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error: any) {
      message.error('Erro ao baixar o documento. Tente novamente.');
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '12px', textAlign: 'center' }}>
        <Spin size="small" />
        <Text type="secondary" style={{ marginLeft: '8px' }}>Verificando documentos existentes...</Text>
      </div>
    );
  }

  if (documentExists && documentInfo) {
    return (
      <div style={{ 
        padding: '16px', 
        backgroundColor: '#f0f9ff', 
        border: '1px solid #0ea5e9', 
        borderRadius: '8px',
        marginBottom: '20px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          {/* Icon */}
          <div style={{
            width: '40px',
            height: '40px',
            backgroundColor: '#0ea5e9',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <FileTextOutlined style={{ color: 'white', fontSize: '18px' }} />
          </div>
          
          {/* Content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
              <div>
                <Text strong style={{ color: '#0c4a6e', fontSize: '14px' }}>
                  Documento da Simulação Carregado
                </Text>
                <div style={{ marginTop: '4px' }}>
                  <Text code style={{ 
                    backgroundColor: '#e0f2fe', 
                    color: '#0369a1', 
                    padding: '2px 6px', 
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}>
                    {documentInfo.filename}
                  </Text>
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button 
                  type="primary"
                  size="small"
                  onClick={handleDownload}
                  icon={<DownloadOutlined />}
                  style={{
                    backgroundColor: '#0ea5e9',
                    borderColor: '#0ea5e9',
                    borderRadius: '6px',
                    height: '28px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}
                >
                  Baixar
                </Button>
                <Button 
                  type="default"
                  size="small"
                  onClick={checkExistingDocument}
                  style={{
                    borderRadius: '6px',
                    height: '28px',
                    fontSize: '12px'
                  }}
                >
                  🔄
                </Button>
              </div>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '6px',
                height: '6px',
                backgroundColor: '#10b981',
                borderRadius: '50%'
              }}></div>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Carregado em {new Date(documentInfo.uploaded_at).toLocaleDateString('pt-PT')} às {new Date(documentInfo.uploaded_at).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}
              </Text>
            </div>
            
            {documentInfo.file_size && (
              <div style={{ marginTop: '4px' }}>
                <Text type="secondary" style={{ fontSize: '11px' }}>
                  Tamanho: {(documentInfo.file_size / 1024 / 1024).toFixed(2)} MB
                </Text>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
};
const { Panel } = Collapse;

interface SolarWizardModalProps {
  visible: boolean;
  onClose: () => void;
  proposalType: string;
}

interface StepData {
  customerInfo: {
    nomeCompleto: string;
    nif: string;
    telefone: string;
    email: string;
    morada: string;
    codigoPostal1: string;
    codigoPostal2: string;
    potenciaContratada: string;
  };
  installationInfo: {
    tipoInstalacao: string;
    tipoEstrutura: string;
    deslocacao: number;
    nivelOrcamento: number;
    equipamento: {
      modulosFV: string;
      inversor: string;
      monitorizacao: string;
      estrutura: string;
      quantidadeModulos: number;
      quantidadeInversor: number;
      quantidadeMonitorizacao: number;
      quantidadeEstrutura: number;
    };
  };
}

const SolarWizardModal: React.FC<SolarWizardModalProps> = ({
  visible,
  onClose,
  proposalType
}) => {
  const { user } = useCognitoAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();
  const [simulationId, setSimulationId] = useState<string | null>(null);
  const [stepLoading, setStepLoading] = useState(false);
  const [pdfDownloadPath, setPdfDownloadPath] = useState<string>('');
  const [stepData, setStepData] = useState<StepData>({
    customerInfo: {
      nomeCompleto: '',
      nif: '',
      telefone: '',
      email: '',
      morada: '',
      codigoPostal1: '',
      codigoPostal2: '',
      potenciaContratada: ''
    },
    installationInfo: {
      tipoInstalacao: '',
      tipoEstrutura: '',
      deslocacao: 0,
      nivelOrcamento: 1,
      equipamento: {
        modulosFV: '',
        inversor: '',
        monitorizacao: '',
        estrutura: '',
        quantidadeModulos: 1,
        quantidadeInversor: 1,
        quantidadeMonitorizacao: 1,
        quantidadeEstrutura: 1
      }
    }
  });
  const [equipamentoOptions, setEquipamentoOptions] = useState<{
    modulosFV: string[];
    inversor: string[];
    monitorizacao: string[];
    estrutura: string[];
    contador: string[];
    quadroProtecao: string[];
    cabos: string[];
  }>({
    modulosFV: [],
    inversor: [],
    monitorizacao: [],
    estrutura: [],
    contador: [],
    quadroProtecao: [],
    cabos: []
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingPowerPoint, setIsGeneratingPowerPoint] = useState(false);
  const [equipamentoExpanded, setEquipamentoExpanded] = useState(true);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeError, setIframeError] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [sessionData, setSessionData] = useState<any>(null);
  const [budgetTableData, setBudgetTableData] = useState<any>(null);
  const [budgetTableLoading, setBudgetTableLoading] = useState(false);
  const [savedClientId, setSavedClientId] = useState<number | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [documentRefreshTrigger, setDocumentRefreshTrigger] = useState(0);



  const steps = [
    {
      title: 'Informação do Cliente',
      icon: <UserOutlined />,
      description: 'Dados pessoais e contacto'
    },
    {
      title: 'Informação sobre a Instalação',
      icon: <HomeOutlined />,
      description: 'Configuração do sistema'
    },
    {
      title: 'Orçamento Inicial',
      icon: <CheckCircleOutlined />,
      description: 'Cálculo e apresentação do orçamento'
    },
    {
      title: 'Documentação e Simulação',
      icon: <HomeOutlined />,
      description: 'Upload de documentos e simulação'
    },
    {
      title: 'Orçamento Final',
      icon: <SunOutlined />,
      description: 'Geração e download do orçamento final'
    }
  ];

  // Function to get equipment options from server-side API - NO FALLBACKS
  const fetchEquipamentoOptions = async () => {
    setIsLoading(true);
    
    // Get current form values for installation type and structure
    const formValues = form.getFieldsValue();
    const tipoInstalacao = formValues.tipoInstalacao;
    const tipoEstrutura = formValues.tipoEstrutura;
    
    if (!tipoInstalacao || !tipoEstrutura) {
      throw new Error('Please select both Tipo de Instalação and Tipo de Estrutura first');
    }
    
    // Get equipment options from server-side API - MUST WORK
    const response = await api.post('/api/solar-server/get-equipment-options', {
      tipoInstalacao: tipoInstalacao,
      tipoEstrutura: tipoEstrutura
    });
    
    if (!response.data.success) {
      throw new Error(`Failed to get equipment options: ${response.data.error}`);
    }
    
    setEquipamentoOptions(response.data.equipment_options);
    setIsLoading(false);
  };

  useEffect(() => {
    if (equipamentoExpanded) {
      // Only fetch options when section is expanded AND form fields are filled
      const formValues = form.getFieldsValue();
      const tipoInstalacao = formValues.tipoInstalacao;
      const tipoEstrutura = formValues.tipoEstrutura;
      
      if (tipoInstalacao && tipoEstrutura) {
        fetchEquipamentoOptions().catch(error => {
          message.error(`Failed to fetch equipment options: ${error.message}`);
          setIsLoading(false);
        });
      }
    }
  }, [equipamentoExpanded, stepData.installationInfo.tipoInstalacao, stepData.installationInfo.tipoEstrutura]);

  // Function to handle installation type changes
  const handleInstallationTypeChange = (value: string) => {
    
    // Update the form data
    const currentFormData = form.getFieldsValue();
    const updatedFormData = { ...currentFormData, tipoInstalacao: value };
    form.setFieldsValue(updatedFormData);
    
    // Update the stepData state
    setStepData(prevData => ({
      ...prevData,
      installationInfo: {
        ...prevData.installationInfo,
        tipoInstalacao: value
      }
    }));
    
    // Refresh equipment options if expanded
    if (equipamentoExpanded) {
      fetchEquipamentoOptions().catch(error => {
        message.error(`Failed to refresh equipment options: ${error.message}`);
        setIsLoading(false);
      });
    }
  };

  // Function to handle structure type changes
  const handleStructureTypeChange = (value: string) => {
    
    // Update the form data
    const currentFormData = form.getFieldsValue();
    const updatedFormData = { ...currentFormData, tipoEstrutura: value };
    form.setFieldsValue(updatedFormData);
    
    // Update the stepData state
    setStepData(prevData => ({
      ...prevData,
      installationInfo: {
        ...prevData.installationInfo,
        tipoEstrutura: value
      }
    }));
    
    // Refresh equipment options if expanded
    if (equipamentoExpanded) {
      fetchEquipamentoOptions().catch(error => {
        message.error(`Failed to refresh equipment options: ${error.message}`);
        setIsLoading(false);
      });
    }
  };

  const handleDeslocacaoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    
    // Update the stepData state
    setStepData(prevData => ({
      ...prevData,
      installationInfo: {
        ...prevData.installationInfo,
        deslocacao: value
      }
    }));
  };

  const handleEquipmentExpand = (expanded: boolean) => {
    setEquipamentoExpanded(expanded);
    
    if (expanded) {
      // Only fetch options when expanded AND form fields are filled
      const formValues = form.getFieldsValue();
      const tipoInstalacao = formValues.tipoInstalacao;
      const tipoEstrutura = formValues.tipoEstrutura;
      
      if (tipoInstalacao && tipoEstrutura) {
        fetchEquipamentoOptions().catch(error => {
          message.error(`Failed to fetch equipment options: ${error.message}`);
          setIsLoading(false);
        });
      }
    }
  };

  const fetchBudgetTableData = async (installationInfo: any) => {
    setBudgetTableLoading(true);
    
    try {
      // Get budget table data from server-side API - NO FALLBACKS
      const response = await api.post('/api/solar-server/get-budget-table', {
        installationInfo: installationInfo
      });
      
      if (!response.data.success) {
        throw new Error(`Server error: ${response.data.error}`);
      }
      
      const budgetData = response.data.budget_table;
      
      setBudgetTableData(budgetData);
      setBudgetTableLoading(false);
    } catch (error) {
      setBudgetTableLoading(false);
      throw error;
    }
  };

  const handleNext = async () => {
    try {
      setStepLoading(true);
      message.loading('Processando passo...', 0);
      
      const values = await form.validateFields();
      
      if (currentStep === 0) {
        message.loading('Salvando informações do cliente...', 0);
        setStepData(prev => ({
          ...prev,
          customerInfo: values
        }));
        message.destroy();
        message.success('Informações do cliente salvas!');
      } else if (currentStep === 1) {
        message.loading('Processando informações da instalação...', 0);
        const updatedInstallationInfo = values;
        setStepData(prev => ({
          ...prev,
          installationInfo: updatedInstallationInfo
        }));
        
        message.loading('Carregando dados do orçamento...', 0);
        // Fetch budget table data - NO FALLBACKS, MUST WORK
        await fetchBudgetTableData(updatedInstallationInfo);
        
        message.loading('Criando cliente potencial...', 0);
        // Save data and create potential client - NO FALLBACKS, MUST WORK
        const saveResponse = await api.post('/api/solar-server/save-potential-client-server', {
          customerInfo: stepData.customerInfo,
          installationInfo: updatedInstallationInfo,
          proposalType: proposalType
        });
        
        if (!saveResponse.data.success) {
          throw new Error(`Failed to save client: ${saveResponse.data.error}`);
        }
        
        message.destroy();
        message.success('Cliente potencial criado/atualizado com sucesso!');
        
        // Store the client ID for later use in data extraction
        if (saveResponse.data.client_id) {
          setSavedClientId(saveResponse.data.client_id);
        }
      } else if (currentStep === 2) {
        message.loading('Preparando simulação...', 0);
        // Step 2 to 3 transition - preparing simulation
        message.destroy();
        message.success('Simulação preparada!');
      } else if (currentStep === 3) {
        message.loading('Processando dados da simulação...', 0);
        // Step 3 to 4 transition - processing simulation data
        message.destroy();
        message.success('Dados da simulação processados!');
      }
      
      setCurrentStep(currentStep + 1);
    } catch (error: any) {
      message.destroy();
      message.error(`Failed to process step: ${error.message}`);
      // Don't advance to next step if there's an error
    } finally {
      setStepLoading(false);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
  };

  // File upload functionality
  const handleFileUpload = async (file: File) => {
    try {
      if (!savedClientId) {
        throw new Error('Cliente não foi salvo ainda. Complete os passos anteriores primeiro.');
      }
      
      setStepLoading(true);
      message.loading('Fazendo upload do arquivo...', 0);
      
      // Create FormData to send the file
      const formData = new FormData();
      formData.append('file', file);
      formData.append('client_id', savedClientId.toString());
      
      // Upload the file to the server
      const response = await api.post('/api/iframe/upload-simulation-document', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      message.destroy();
      
      if (response.data.success) {
        message.success('Documento da simulação enviado com sucesso!');
        setUploadedFile(file);
        // Trigger refresh of document check
        setDocumentRefreshTrigger(prev => prev + 1);
      } else {
        throw new Error(response.data.error || 'Erro ao fazer upload do arquivo');
      }
      
    } catch (error: any) {
      message.destroy();
      message.error(`Erro no upload: ${error.message}`);
    } finally {
      setStepLoading(false);
    }
  };



  const handleClose = async () => {
    setCurrentStep(0);
    form.resetFields();
    setStepData({
      customerInfo: {
        nomeCompleto: '',
        nif: '',
        telefone: '',
        email: '',
        morada: '',
        codigoPostal1: '',
        codigoPostal2: '',
        potenciaContratada: ''
      },
      installationInfo: {
        tipoInstalacao: '',
        tipoEstrutura: '',
        deslocacao: 0,
        nivelOrcamento: 1,
        equipamento: {
          modulosFV: '',
          inversor: '',
          monitorizacao: '',
          estrutura: '',
          quantidadeModulos: 1,
          quantidadeInversor: 1,
          quantidadeMonitorizacao: 1,
          quantidadeEstrutura: 1
        }
      }
    });
    setEquipamentoOptions({
      modulosFV: [],
      inversor: [],
      monitorizacao: [],
      estrutura: [],
      contador: [],
      quadroProtecao: [],
      cabos: []
    });
    setEquipamentoExpanded(true);
    setSimulationId(null);
    setBudgetTableData(null);
    setUploadedFile(null);
    onClose();
  };


  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="wizard-step-content">
            <Title level={3} className="step-title">
              Informação do Cliente
            </Title>
            <Text className="step-description">
              Preencha os dados pessoais e de contacto do cliente
            </Text>
            
            <Form
              form={form}
              layout="vertical"
              initialValues={stepData.customerInfo}
              className="wizard-form"
            >
              <Row gutter={[24, 16]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="nomeCompleto"
                    label="Nome Completo"
                    rules={[{ required: true, message: 'Por favor, insira o nome completo' }]}
                  >
                    <Input 
                      size="large" 
                      placeholder="Nome completo do cliente"
                      className="wizard-input"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="nif"
                    label="NIF"
                    rules={[{ required: true, message: 'Por favor, insira o NIF' }]}
                  >
                    <Input 
                      size="large" 
                      placeholder="Número de identificação fiscal"
                      className="wizard-input"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="telefone"
                    label="Número de Telefone"
                    rules={[{ required: true, message: 'Por favor, insira o número de telefone' }]}
                  >
                    <Input 
                      size="large" 
                      placeholder="Número de telefone"
                      className="wizard-input"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="email"
                    label="E-mail"
                    rules={[
                      { required: true, message: 'Por favor, insira o e-mail' },
                      { type: 'email', message: 'Por favor, insira um e-mail válido' }
                    ]}
                  >
                    <Input 
                      size="large" 
                      placeholder="Endereço de e-mail"
                      className="wizard-input"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item
                    name="morada"
                    label="Morada Completa"
                    rules={[{ required: true, message: 'Por favor, insira a morada completa' }]}
                  >
                    <Input 
                      size="large" 
                      placeholder="Morada completa"
                      className="wizard-input"
                    />
                  </Form.Item>
                </Col>
                                 <Col xs={24} md={6}>
                   <Form.Item
                     name="codigoPostal1"
                     label="Código Postal"
                     rules={[{ required: true, message: 'Por favor, insira o código postal' }]}
                   >
                     <Input 
                       size="large" 
                       placeholder="0000"
                       className="wizard-input"
                       maxLength={4}
                     />
                   </Form.Item>
                 </Col>
                 <Col xs={24} md={6}>
                   <Form.Item
                     name="codigoPostal2"
                     label=" "
                     rules={[{ required: true, message: 'Por favor, insira o código postal' }]}
                   >
                     <Input 
                       size="large" 
                       placeholder="000"
                       className="wizard-input"
                       maxLength={3}
                     />
                   </Form.Item>
                 </Col>
                 <Col xs={24} md={12}>
                   <Form.Item
                     name="potenciaContratada"
                     label="Potência Contratada"
                     rules={[{ required: true, message: 'Por favor, insira a potência contratada' }]}
                   >
                     <Input 
                       size="large" 
                       placeholder="Ex: 6.9 kVA"
                       className="wizard-input"
                     />
                   </Form.Item>
                 </Col>
              </Row>
            </Form>
          </div>
        );

      case 1:
        return (
          <div className="wizard-step-content">
            <Title level={3} className="step-title">
              Informação sobre a Instalação
            </Title>
            <Text className="step-description">
              Configure o tipo de instalação e equipamento necessário
            </Text>
            
            <Form
              form={form}
              layout="vertical"
              initialValues={stepData.installationInfo}
              className="wizard-form"
            >
              <Row gutter={[24, 16]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="tipoInstalacao"
                    label="Tipo de Instalação"
                    rules={[{ required: true, message: 'Por favor, selecione o tipo de instalação' }]}
                  >
                    <Select 
                      size="large" 
                      placeholder="Selecione o tipo de instalação"
                      className="wizard-select"
                      onChange={handleInstallationTypeChange}
                    >
                      <Option value="monofasico">Monofásico</Option>
                      <Option value="trifasico">Trifásico</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="tipoEstrutura"
                    label="Tipo de Estrutura"
                    rules={[{ required: true, message: 'Por favor, selecione o tipo de estrutura' }]}
                  >
                    <Select 
                      size="large" 
                      placeholder="Selecione o tipo de estrutura"
                      className="wizard-select"
                      onChange={handleStructureTypeChange}
                    >
                      <Option value="coplanar">Coplanar</Option>
                      <Option value="triangular">Triangular</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              {/* New fields: Deslocação and Nível do Orçamento */}
              <Row gutter={[24, 16]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="deslocacao"
                    label="Deslocação (km)"
                    rules={[
                      { required: true, message: 'Por favor, insira a distância em km' },
                      { 
                        validator: (_, value) => {
                          if (value === undefined || value === null || value === '') {
                            return Promise.resolve();
                          }
                          const numValue = Number(value);
                          if (isNaN(numValue)) {
                            return Promise.reject(new Error('Por favor, insira um número válido'));
                          }
                          if (numValue < 0) {
                            return Promise.reject(new Error('A distância deve ser um número positivo'));
                          }
                          return Promise.resolve();
                        }
                      }
                    ]}
                  >
                    <Input 
                      size="large" 
                      type="number"
                      min={0}
                      placeholder="Ex: 50"
                      className="wizard-input"
                      onChange={handleDeslocacaoChange}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="nivelOrcamento"
                    label="Nível do Orçamento"
                    rules={[{ required: true, message: 'Por favor, selecione o nível do orçamento' }]}
                  >
                    <div className="nivel-orcamento-container">
                      {[1, 2, 3].map((level) => (
                        <div
                          key={level}
                          className={`nivel-orcamento-square ${form.getFieldValue('nivelOrcamento') === level ? 'selected' : ''}`}
                          onClick={() => {
                            form.setFieldsValue({ nivelOrcamento: level });
                            setStepData(prevData => ({
                              ...prevData,
                              installationInfo: {
                                ...prevData.installationInfo,
                                nivelOrcamento: level
                              }
                            }));
                          }}
                        >
                          <span className="nivel-number">{level}</span>
                        </div>
                      ))}
                    </div>
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <Collapse 
                activeKey={equipamentoExpanded ? ['equipamento'] : []}
                                      onChange={(keys) => handleEquipmentExpand(keys.includes('equipamento'))}
                className="equipamento-collapse"
              >
                <Panel 
                  header={
                    <div className="equipamento-header">
                      <SettingOutlined />
                      <span>Equipamento</span>
                      {isLoading && <LoadingOutlined className="loading-icon" />}
                    </div>
                  } 
                  key="equipamento"
                  className="equipamento-panel"
                >
                                                        <div className="equipamento-grid">
                     <Row gutter={[16, 16]}>
                       {/* First Row */}
                       <Col xs={24} md={10}>
                         <Form.Item
                           name={['equipamento', 'modulosFV']}
                           label="Módulos FV"
                           rules={[{ required: true, message: 'Selecione os módulos FV' }]}
                         >
                           <Select 
                             size="large" 
                             placeholder="Selecione"
                             className="wizard-select modulos-select"
                             loading={isLoading}
                           >
                            {equipamentoOptions.modulosFV.map((option, index) => (
                              <Option key={`modulosFV-${index}`} value={option}>
                                {option}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                       <Col xs={24} md={2}>
                         <Form.Item
                           name={['equipamento', 'quantidadeModulos']}
                           label="Quantidade"
                         >
                          <Input 
                            type="number" 
                            size="large" 
                            min={1}
                            className="wizard-input"
                          />
                        </Form.Item>
                      </Col>
                       <Col xs={24} md={10}>
                         <Form.Item
                           name={['equipamento', 'inversor']}
                           label="Inversor"
                           rules={[{ required: true, message: 'Selecione o inversor' }]}
                         >
                           <Select 
                             size="large" 
                             placeholder="Selecione"
                             className="wizard-select inversor-select"
                             loading={isLoading}
                           >
                            {equipamentoOptions.inversor.map((option, index) => (
                              <Option key={`inversor-${index}`} value={option}>
                                {option}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                       <Col xs={24} md={2}>
                         <Form.Item
                           name={['equipamento', 'quantidadeInversor']}
                           label="Quantidade"
                         >
                          <Input 
                            type="number" 
                            size="large" 
                            min={1}
                            className="wizard-input"
                          />
                        </Form.Item>
                      </Col>
                     </Row>
                     
                     <Row gutter={[16, 16]}>
                       {/* Second Row */}
                       <Col xs={24} md={10}>
                         <Form.Item
                           name={['equipamento', 'estrutura']}
                           label="Estrutura"
                           rules={[{ required: true, message: 'Selecione a estrutura' }]}
                         >
                           <Select 
                             size="large" 
                             placeholder="Selecione"
                             className="wizard-select estrutura-select"
                             loading={isLoading}
                           >
                            {equipamentoOptions.estrutura.map((option, index) => (
                              <Option key={`estrutura-${index}`} value={option}>
                                {option}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                       <Col xs={24} md={2}>
                         <Form.Item
                           name={['equipamento', 'quantidadeEstrutura']}
                           label="Quantidade"
                         >
                          <Input 
                            type="number" 
                            size="large" 
                            min={1}
                            className="wizard-input"
                          />
                        </Form.Item>
                      </Col>
                       <Col xs={24} md={10}>
                         <Form.Item
                           name={['equipamento', 'monitorizacao']}
                           label="Monitorização"
                           rules={[{ required: true, message: 'Selecione a monitorização' }]}
                         >
                           <Select 
                             size="large" 
                             placeholder="Selecione"
                             className="wizard-select monitorizacao-select"
                             loading={isLoading}
                           >
                            {equipamentoOptions.monitorizacao.map((option, index) => (
                              <Option key={`monitorizacao-${index}`} value={option}>
                                {option}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                       <Col xs={24} md={2}>
                         <Form.Item
                           name={['equipamento', 'quantidadeMonitorizacao']}
                           label="Quantidade"
                         >
                          <Input 
                            type="number" 
                            size="large" 
                            min={1}
                            className="wizard-input"
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>
                </Panel>
              </Collapse>
            </Form>
          </div>
        );

      case 2:
        return (
          <div className="wizard-step-content">
            <Title level={3} className="step-title">
              Orçamento Inicial
            </Title>
            <Text className="step-description">
              Visualize e confirme o orçamento calculado para a instalação
            </Text>
            
            <Form
              form={form}
              layout="vertical"
              className="wizard-form"
            >
              <Row gutter={[24, 16]}>
                <Col xs={24}>
                  <div className="budget-table-container">
                    {budgetTableLoading ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin size="large" />
                        <div style={{ marginTop: '16px' }}>Carregando orçamento...</div>
                      </div>
                    ) : budgetTableData && budgetTableData.header && budgetTableData.footer && budgetTableData.rows ? (
                      <table className="budget-table">
                        <thead>
                          <tr className="table-header">
                            <th className="qtd-column">{budgetTableData.header.qtd}</th>
                            <th className="material-column">{budgetTableData.header.material}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {budgetTableData.rows.map((row: any, index: number) => (
                            <tr key={index} className="table-row">
                              <td className="qtd-cell">{row.qtd}</td>
                              <td className="material-cell">{row.material}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="table-footer">
                            <td className="qtd-cell total-cell">{budgetTableData.footer.qtd}</td>
                            <td className="material-cell total-cell">{budgetTableData.footer.material}</td>
                          </tr>
                        </tfoot>
                      </table>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                        Nenhum dado de orçamento disponível
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </Form>
          </div>
        );

      case 3:
         return (
           <div className="wizard-step-content">
             <Title level={3} className="step-title">
               Documentação e Simulação
             </Title>
             <Text className="step-description">
               Visualize e extraia dados da simulação Sunny Design
             </Text>
             
             <Form
               form={form}
               layout="vertical"
               className="wizard-form"
             >
                               <Row gutter={[24, 16]}>
                  <Col xs={24}>
                                         <Form.Item
                       name="iframeContent"
                       label="Simulação"
                     >
                       <div className={`iframe-container ${iframeLoaded ? 'loaded' : ''} ${iframeError ? 'error' : ''}`}>
                         {!isLoggedIn && (
                           <div style={{ marginBottom: '12px', textAlign: 'center' }}>
                             <Text style={{ marginBottom: '8px', display: 'block' }}>
                               Para aceder à simulação Sunny Design, faça login primeiro:
                             </Text>
                             <Button
                               type="primary"
                               onClick={() => {
                                 // Open Sunny Design in new window and set up session detection
                                 const newWindow = window.open('https://www.sunnydesignweb.com/sdweb/', '_blank');
                                 
                                 // Set up interval to check if user has logged in
                                 const checkLoginInterval = setInterval(() => {
                                   try {
                                     // Try to access the new window's location
                                     if (newWindow && !newWindow.closed) {
                                       // Check if we can access the window (means user is logged in)
                                       const currentUrl = newWindow.location.href;
                                       if (currentUrl.includes('sunnydesignweb.com') && !currentUrl.includes('login')) {
                                         // User has logged in, capture session data
                                         
                                         // Get cookies from the new window
                                         const cookies = newWindow.document.cookie;
                                         const localStorage = newWindow.localStorage;
                                         const sessionStorage = newWindow.sessionStorage;
                                         
                                         // Store session data
                                         setSessionData({
                                           cookies,
                                           localStorage: Object.fromEntries(Object.entries(localStorage)),
                                           sessionStorage: Object.fromEntries(Object.entries(sessionStorage))
                                         });
                                         
                                         setIsLoggedIn(true);
                                         clearInterval(checkLoginInterval);
                                         
                                         // Close the new window
                                         newWindow.close();
                                         
                                         message.success('Login detectado! Atualizando iframe...');
                                       }
                                     } else {
                                       // Window was closed
                                       clearInterval(checkLoginInterval);
                                     }
                                   } catch (error) {
                                     // Cross-origin error means user is logged in
                                       setIsLoggedIn(true);
                                       clearInterval(checkLoginInterval);
                                       
                                       if (newWindow && !newWindow.closed) {
                                         newWindow.close();
                                       }
                                       
                                       message.success('Login detectado! Atualizando iframe...');
                                   }
                                 }, 1000); // Check every second
                                 
                                 // Clear interval after 5 minutes to prevent memory leaks
                                 setTimeout(() => {
                                   clearInterval(checkLoginInterval);
                                 }, 300000);
                               }}
                               style={{ marginRight: '8px' }}
                             >
                               Fazer Login no Sunny Design
                             </Button>
                           </div>
                         )}
                         <iframe
                           src="https://www.sunnydesignweb.com/sdweb/"
                           title="Simulação Solar"
                           className="wizard-iframe"
                           frameBorder="0"
                           allowFullScreen
                           sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-top-navigation allow-top-navigation-by-user-activation allow-same-origin"
                           referrerPolicy="origin"
                           loading="lazy"
                           onLoad={() => {
                             setIframeLoaded(true);
                             setIframeError(false);
                           }}
                           onError={(e) => {
                             // Error handling
                             setIframeError(true);
                             setIframeLoaded(false);
                           }}
                         />
                       </div>
                     </Form.Item>
                  </Col>
                </Row>

                {/* File Upload Section */}
                <Row gutter={[24, 16]}>
                  <Col xs={24}>
                    <div className="file-upload-section">
                      <h4>📄 Upload de Documento da Simulação</h4>
                      <p>Faça upload do documento da simulação Sunny Design para incluir no orçamento final:</p>
                      
                      {/* Check if document already exists */}
                      {savedClientId && (
                        <div style={{ marginBottom: '16px' }}>
                          <CheckExistingDocument 
                            clientId={savedClientId} 
                            onDocumentFound={setUploadedFile}
                            refreshTrigger={documentRefreshTrigger}
                          />
                        </div>
                      )}
                      
                      <Form.Item
                        name="simulationDocument"
                        label="Documento da Simulação"
                        rules={[{ required: true, message: 'Por favor, faça upload do documento da simulação' }]}
                      >
                        <Upload.Dragger
                          name="file"
                          multiple={false}
                          accept=".pdf,.doc,.docx,.xlsx,.xls"
                          beforeUpload={(file) => {
                            // Store the file and upload it
                            form.setFieldsValue({ simulationDocument: file });
                            handleFileUpload(file);
                            return false; // Prevent automatic upload
                          }}
                          onRemove={() => {
                            form.setFieldsValue({ simulationDocument: null });
                            setUploadedFile(null);
                          }}
                          showUploadList={{
                            showPreviewIcon: true,
                            showRemoveIcon: true,
                            showDownloadIcon: false,
                          }}
                        >
                          <p className="ant-upload-drag-icon">
                            📄
                          </p>
                          <p className="ant-upload-text">
                            Clique ou arraste o arquivo para esta área para fazer upload
                          </p>
                          <p className="ant-upload-hint">
                            Suporte para PDF, DOC, DOCX, XLSX, XLS. Tamanho máximo: 10MB
                          </p>
                        </Upload.Dragger>
                      </Form.Item>
                      
                    </div>
                  </Col>
                </Row>
             </Form>
           </div>
         );

      case 4:
        return (
          <div className="wizard-step-content">
            <Title level={3} className="step-title">
              Orçamento Final
            </Title>
            <Text className="step-description">
              Gere e descarregue o orçamento final com todas as informações extraídas
            </Text>
            
            <div className="pdf-generation-container">
              <div className="generation-info">
                <h4>📄 Geração do Orçamento Final</h4>
                <p>O orçamento final será gerado com todas as informações coletadas:</p>
                
                <div className="info-sections">
                  <div className="info-section">
                    <h5>📋 Informações do Cliente</h5>
                    <ul>
                      <li>Dados pessoais e de contacto</li>
                      <li>Morada completa</li>
                      <li>Potência contratada</li>
                    </ul>
                  </div>

                  <div className="info-section">
                    <h5>⚡ Equipamento e Especificações</h5>
                    <ul>
                      <li>Módulos fotovoltaicos</li>
                      <li>Inversores e monitorização</li>
                      <li>Estrutura e cabos</li>
                      <li>Quadros de proteção</li>
                    </ul>
                  </div>

                  <div className="info-section">
                    <h5>💰 Análise de Custos</h5>
                    <ul>
                      <li>Breakdown detalhado de custos</li>
                      <li>Custos de instalação (mão-de-obra, DGEG, percurso)</li>
                      <li>Custos de alojamento (estadia)</li>
                      <li>IVA e totais finais</li>
                    </ul>
                  </div>

                  <div className="info-section">
                    <h5>📊 Dados de Simulação</h5>
                    <ul>
                      <li>Análise financeira (poupanças anuais e 20 anos)</li>
                      <li>Distribuição energética</li>
                      <li>Métricas de performance</li>
                      <li>Gráficos e visualizações</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="generation-actions">
                <Button
                  type="primary"
                  size="large"
                  icon={<SunOutlined />}
                  className="wizard-button wizard-button-primary"
                  loading={isGeneratingPowerPoint}
                  disabled={isGeneratingPowerPoint}
                  onClick={async () => {
                    try {
                      setIsGeneratingPowerPoint(true);
                      message.loading('🔄 A gerar PowerPoint...', 0);
                      
                      // Import the OrcamentoFinalStep component dynamically
                      const { default: OrcamentoFinalStep } = await import('./OrcamentoFinalStep');
                      
                      // Use the saved client ID
                      if (!savedClientId) {
                        message.error('Cliente não foi salvo ainda. Complete os passos anteriores primeiro.');
                        return;
                      }
                      const clientId = savedClientId;
                      
                      // Generate PowerPoint
                      const { IframeService } = await import('../services/iframeService');
                      await IframeService.generateOrcamentoFinal(clientId);
                      
                      message.destroy(); // Clear loading message
                      message.success('✅ PowerPoint gerado e descarregado com sucesso!');
                    } catch (error) {
                      message.destroy(); // Clear loading message
                      message.error('❌ Erro ao gerar o PowerPoint. Tente novamente.');
                    } finally {
                      setIsGeneratingPowerPoint(false);
                    }
                  }}
                >
                  {isGeneratingPowerPoint ? '🔄 A gerar PowerPoint...' : '📄 Gerar e Descarregar Orçamento Final'}
                </Button>
              </div>
            </div>

            <div className="step-footer">
              <div className="completion-info">
                <h4>✅ Processo Concluído</h4>
                <p>Após o download do orçamento final, o processo estará completo. O cliente receberá um documento profissional com todas as informações necessárias para tomar uma decisão informada sobre o sistema fotovoltaico.</p>
              </div>
            </div>
          </div>
        );

       default:
         return null;
     }
   };

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={null}
      width="90%"
      style={{ maxWidth: '1200px' }}
      className="solar-wizard-modal"
      closeIcon={<CloseOutlined style={{ color: 'white', fontSize: '1.5rem' }} />}
    >
      <div className="wizard-background">
        <div className="wizard-content">
          <div className="wizard-header">
            <div className="wizard-header-icon">
              <SunOutlined />
            </div>
            <Title level={2} className="wizard-title">
              Proposta sem Bateria - Simulador
            </Title>
            <Text className="wizard-subtitle">
              Configure o seu sistema solar passo a passo
            </Text>
          </div>

          <div className="wizard-steps-container">
            <Steps 
              current={currentStep} 
              items={steps}
              className="wizard-steps"
            />
          </div>

          <div className="wizard-body">
            {renderStepContent()}
          </div>

          <div className="wizard-footer">
            <Space size="large">
              {currentStep > 0 && (
                <Button
                  size="large"
                  icon={<ArrowLeftOutlined />}
                  onClick={handlePrevious}
                  className="wizard-button wizard-button-secondary"
                  disabled={stepLoading}
                >
                  Anterior
                </Button>
              )}
              
                             {currentStep < steps.length - 1 ? (
                 <Button
                   type="primary"
                   size="large"
                   icon={<ArrowRightOutlined />}
                   onClick={handleNext}
                   className="wizard-button wizard-button-primary"
                   loading={stepLoading}
                   disabled={stepLoading}
                 >
                   {stepLoading ? 'Processando...' : 'Próximo'}
                 </Button>
               ) : (
                 <Button
                   type="primary"
                   size="large"
                   icon={<CheckCircleOutlined />}
                   className="wizard-button wizard-button-success"
                 >
                   Finalizar
                 </Button>
               )}
            </Space>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SolarWizardModal; 
